import {
  useState,
  useEffect,
  useRef,
  FunctionComponent,
  useCallback,
} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import Axios, { AxiosResponse } from "axios";
import { StateLookup, STATES } from "../utils/states";
import { Icon } from "../utils/Icons";
import PhoneField from "./phoneField";

interface Props {
  close: () => void;
  storeState: string;
  storeId: number;
  setIsLoading?: Function;
  setResponse?: Function;
  setDisclaimerOpen: Function;
}

export interface State {
  productName: string;
  maxAmount: number;
  minAmount: number;
  isStore: boolean;
  isWeb: boolean;
}

interface ShortAppDetails {
  campaignID: string;
  canPrequal: boolean;
  captchaKey: string;
  isAjax: boolean;
  leadType: string;
  loanType: string;
  refererUrl: string;
  source: string;
  subID: string;
}

interface InputValidity {
  firstNameValid: boolean | null;
  lastNameValid: boolean | null;
  emailValid: boolean | null;
  homePhoneValid: boolean | null;
  addressValid: boolean | null;
  cityValid: boolean | null;
  stateValid: boolean | null;
  zipCodeValid: boolean | null;
  recaptchaValid: boolean | null;
}

const ShortApp: FunctionComponent<Props> = (props: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [shortAppDetails, setShortAppDetails] = useState<ShortAppDetails>();
  const [currentAddress, setCurrentAddress] = useState<string>("");
  const [currentCity, setCurrentCity] = useState<string>("");
  const [currentState, setCurrentState] = useState<string>("");
  const [currentZip, setCurrentZip] = useState<string>("");
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const [submitText, setSubmitText] = useState<string>(
    "Please fill out all fields"
  );
  const [response, setResponse] = useState<AxiosResponse | null>(null);
  const [value, setValue] = useState("");
  const [inputValid, setInputValid] = useState<InputValidity>({
    firstNameValid: null,
    lastNameValid: null,
    emailValid: null,
    homePhoneValid: null,
    addressValid: null,
    cityValid: null,
    stateValid: null,
    zipCodeValid: null,
    recaptchaValid: null,
  });

  const autoPopulateFields = (node: HTMLInputElement, paramName: string) => {
    if (typeof window !== "undefined") {
      queryString = new URLSearchParams(window.location.search);
      if (queryString.has(paramName)) {
        node.value = queryString.get(paramName);
      }
    }
  };

  const searchRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const communicationAgreeRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const firstnameRef = useCallback((node: HTMLInputElement) => {
    autoPopulateFields(node, "firstname");
  }, []);
  const lastnameRef = useCallback((node: HTMLInputElement) => {
    autoPopulateFields(node, "lastname");
  }, []);
  const emailRef = useCallback((node: HTMLInputElement) => {
    autoPopulateFields(node, "email");
  }, []);
  const phoneRef = useCallback((node: HTMLInputElement) => {
    autoPopulateFields(node, "phone");
  }, []);
  const addressRef = useCallback((node: HTMLInputElement) => {
    autoPopulateFields(node, "street");
  }, []);
  const cityRef = useCallback((node: HTMLInputElement) => {
    autoPopulateFields(node, "city");
  }, []);
  const stateRef = useCallback((node: HTMLInputElement) => {
    autoPopulateFields(node, "province");
  }, []);
  const zipRef = useCallback((node: HTMLInputElement) => {
    autoPopulateFields(node, "postalcode");
  }, []);

  let queryString: URLSearchParams;

  // useEffect(() => {
  //   if (typeof window !== "undefined") {
  //     queryString = new URLSearchParams(window.location.search);
  //     console.log(queryString.get("firstname"));
  //     console.log(firstnameRef.current);
  //     if (queryString.has("firstname") && firstnameRef.current) {
  //       firstnameRef.current.value = queryString.get("firstname");
  //     }
  //   }
  // }, []);

  const google =
    typeof window !== "undefined" ? window.google : (null as never);
  let searchBox: google.maps.places.Autocomplete;
  let searchBoxListener: google.maps.MapsEventListener;
  const states = STATES;

  const enableEnterKey = (input: HTMLDivElement) => {
    /* Store original event listener */
    const _addEventListener = input.addEventListener;

    const addEventListenerWrapper = (type: string, listener: EventListener) => {
      if (type === "keydown") {
        /* Store existing listener function */
        const _listener = listener;
        listener = ((event: KeyboardEvent) => {
          /* Simulate a 'down arrow' keypress if no address has been selected */
          const suggestionSelected =
            document.getElementsByClassName("pac-item-selected").length;
          if (event.key === "Enter" && suggestionSelected == 0) {
            const e = new KeyboardEvent("keydown", {
              key: "ArrowDown",
              code: "ArrowDown",
            });
            Object.defineProperty(e, "keyCode", { value: 40 });
            _listener.apply(input, [e]);
          }
          _listener.apply(input, [event]);
        }) as EventListener;
      }
      _addEventListener.apply(input, [type, listener]);
    };
    input.addEventListener = addEventListenerWrapper;
  };

  const handlePlaceChanged = () => {
    const place = searchBox.getPlace();
    if (place.address_components) {
      const streetNumberComponent = place.address_components.find(
        (component: google.maps.GeocoderAddressComponent) =>
          component.types.includes("street_number")
      );
      const streetNumber = streetNumberComponent
        ? streetNumberComponent.long_name + " "
        : "";
      const routeComponent = place.address_components.find(
        (component: google.maps.GeocoderAddressComponent) =>
          component.types.includes("route")
      );
      const route = routeComponent ? routeComponent.long_name : "";
      const address = streetNumber + route;
      const cityComponent = place.address_components.find(
        (component: google.maps.GeocoderAddressComponent) =>
          component.types.includes("locality")
      );
      const city = cityComponent ? cityComponent.long_name : null;
      const stateComponent = place.address_components.find(
        (component: google.maps.GeocoderAddressComponent) =>
          component.types.includes("administrative_area_level_1")
      );
      let state = null;
      if (stateComponent) {
        for (let i = 0; i < states.length; i++) {
          if (states[i].name == stateComponent.long_name) {
            state = states[i].abbr;
          }
        }
      }
      const zipComponent = place.address_components.find(
        (component: google.maps.GeocoderAddressComponent) =>
          component.types.includes("postal_code")
      );
      const zip = zipComponent ? zipComponent.long_name : null;

      setCurrentAddress(address);
      city !== null ? setCurrentCity(city) : void 0;
      state !== null ? setCurrentState(state) : void 0;
      zip !== null ? setCurrentZip(zip) : void 0;
    }
  };

  const allFilled = () => {
    if (
      formRef.current &&
      formRef.current.checkValidity() &&
      inputValid.recaptchaValid &&
      submitRef.current
    ) {
      setSubmitDisabled(false);
      setSubmitText("Start Your Application");
    } else {
      setSubmitDisabled(true);
      setSubmitText("Please Fill Out All Fields");
    }
  };

  const validateInput = (
    e: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLSelectElement>
  ) => {
    const postalCode = zipRef.current?.value ?? "";
    if (postalCode.length == 6 && postalCode[3] !== " ")
      zipRef.current.value =
        postalCode.substring(0, 3) + " " + postalCode.substring(3);
    const key: keyof InputValidity = e.currentTarget
      .name as keyof InputValidity;
    if (e.type == "change") {
      if (inputValid[key] !== null) {
        if (e.currentTarget.checkValidity()) {
          e.currentTarget.style.backgroundColor = "#fff";
        }
      }
      allFilled();
    } else if (e.type == "focus") {
      if (inputValid[key]) {
        if (!e.currentTarget.checkValidity()) {
          e.currentTarget.reportValidity();
        }
      }
    } else if (e.type == "blur") {
      if (!e.currentTarget.checkValidity()) {
        e.currentTarget.style.backgroundColor = "#ffe1e1";
      }
      if (
        inputValid[key] !== e.currentTarget.checkValidity() &&
        inputValid[key] !== true
      ) {
        e.currentTarget.reportValidity();
      }
      setInputValid({ ...inputValid, [key]: e.currentTarget.checkValidity() });
    }
  };

  const onFormEnter = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.which === 13) {
      e.preventDefault();
    }
  };

  const processForm = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const postalCode = zipRef.current?.value ?? "";
    if (postalCode.length == 6 && postalCode[3] !== " ")
      zipRef.current.value =
        postalCode.substring(0, 3) + " " + postalCode.substring(3);
    const form: FormData = new FormData(formRef.current as HTMLFormElement);
    const url = "https://leads.cashmoney.ca/buylead.axd";
    const headers = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    };
    const response: AxiosResponse | null = await Axios.post(url, form, headers);
    setResponse(response);
    props.setResponse && props.setResponse(response);
    return false;
  };

  const fetchStates = () => {
    Axios.get("/api/shortapp/" + props.storeId + ".json")
      .then((response) => response.data)
      .then((data) => {
        setShortAppDetails(data);
        setIsLoading(false);
        props.setIsLoading && props.setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        props.setIsLoading && props.setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (addressRef) {
      const options = {
        types: ["address"],
        componentRestrictions: { country: "ca" },
      };
      searchBox = new google.maps.places.Autocomplete(addressRef, options);
      searchBox.setFields(["address_components"]);
      searchBoxListener = searchBox.addListener(
        "place_changed",
        handlePlaceChanged
      );
      enableEnterKey(addressRef);
    }
  }, [addressRef]);

  useEffect(() => {
    allFilled();
  }, [inputValid.recaptchaValid]);

  useEffect(() => {
    if (currentAddress !== null && searchRef !== null) {
      searchRef.value = currentAddress;
    }
  }, [currentAddress]);

  useEffect(() => {
    if (currentCity !== null && cityRef !== null) {
      cityRef.value = currentCity;
    }
  }, [currentCity]);

  useEffect(() => {
    if (currentState !== null && stateRef !== null) {
      stateRef.value = currentState;
    }
  }, [currentState]);

  useEffect(() => {
    if (currentZip !== null && zipRef !== null) {
      zipRef.value = currentZip;
    }
  }, [currentZip]);

  useEffect(() => {
    return () => {
      searchBoxListener?.remove();
    };
  }, []);

  return isLoading || states.length == 0 ? (
    <div
      sx={{
        height: "80%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingLeft: "40px",
        paddingRight: "40px",
        fontSize: "32px",
      }}
    >
      <Icon
        alt="Loading"
        name="spinner"
        fill="#000"
        sx={{
          variant: "spinner",
        }}
      />
    </div>
  ) : response?.statusText == "OK" ? (
    <div
      sx={{
        marginLeft: "100px",
        marginRight: "100px",
        textAlign: "center",
      }}
    >
      <h2
        sx={{
          color: "#2B2B2B",
          fontSize: "24px",
          margin: "0 0 20px 0",
        }}
      >
        Thank you for Applying!
      </h2>
      <p
        sx={{
          fontSize: "1rem",
        }}
      >
        A customer representative will contact you soon!
      </p>
    </div>
  ) : response ? (
    <div
      sx={{
        marginLeft: "100px",
        marginRight: "100px",
        textAlign: "center",
      }}
    >
      <h2
        sx={{
          color: "#2B2B2B",
          fontSize: "24px",
          margin: "10px 0 20px 0",
        }}
      >
        Your Request Failed
      </h2>
      <p
        sx={{
          fontSize: "1rem",
        }}
      >
        Contact <a href="/contact-us/">Customer Service</a> for more
        information.
      </p>
    </div>
  ) : (
    <div
      sx={{
        padding: "0px 40px 40px 40px",
        backgroundColor: "#fff",
      }}
    >
      <h2
        sx={{
          fontWeight: "600",
          lineHeight: "1.2",
          fontFamily: "heading",
          fontSize: 4,
          textAlign: "center",
          color: "primary",
        }}
      >
        Start your application online and finish it at a store near you.
      </h2>
      <form
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        role="button"
        ref={formRef}
        onKeyPress={(e) => onFormEnter(e)}
        sx={{
          marginBottom: "40px",
        }}
      >
        <div
          sx={{
            marginTop: "25px",
            width: "100%",
          }}
        >
          <div
            sx={{
              width: "100%",
              marginTop: "15px",
            }}
          >
            <label
              htmlFor="firstname"
              sx={{
                width: "100%",
              }}
            >
              First name
            </label>
            <input
              type="text"
              ref={firstnameRef}
              required
              name="firstname"
              placeholder="First name"
              onChange={(e) => validateInput(e)}
              onBlur={(e) => validateInput(e)}
              onFocus={(e) => validateInput(e)}
              sx={{
                width: "100%",
                outline: "none",
                border: "solid 1px #ccc",
                borderRadius: "4px",
                fontSize: "16px",
                padding: "10px",
              }}
            ></input>
          </div>
          <div
            sx={{
              width: "100%",
              marginTop: "15px",
            }}
          >
            <label
              htmlFor="lastname"
              sx={{
                width: "100%",
              }}
            >
              Last name
            </label>
            <input
              type="text"
              ref={lastnameRef}
              required
              name="lastname"
              placeholder="Last name"
              onChange={(e) => validateInput(e)}
              onBlur={(e) => validateInput(e)}
              onFocus={(e) => validateInput(e)}
              sx={{
                width: "100%",
                outline: "none",
                border: "solid 1px #ccc",
                borderRadius: "4px",
                fontSize: "16px",
                padding: "10px",
              }}
            ></input>
          </div>
        </div>

        <div
          sx={{
            marginTop: "25px",
            width: "100%",
          }}
        >
          <div
            sx={{
              width: "100%",
              marginTop: "15px",
            }}
          >
            <label
              htmlFor="email"
              sx={{
                width: "100%",
              }}
            >
              Email address
            </label>
            <input
              type="email"
              ref={emailRef}
              required
              name="email"
              placeholder="email@example.com"
              onChange={(e) => validateInput(e)}
              onBlur={(e) => validateInput(e)}
              onFocus={(e) => validateInput(e)}
              sx={{
                width: "100%",
                outline: "none",
                border: "solid 1px #ccc",
                borderRadius: "4px",
                fontSize: "16px",
                padding: "10px",
              }}
            ></input>
          </div>
          <div
            sx={{
              width: "100%",
              marginTop: "15px",
            }}
          >
            <label
              htmlFor="homePhone"
              sx={{
                width: "100%",
              }}
            >
              Phone number
            </label>
            <PhoneField
              initialValue={() => {
                if (typeof window !== "undefined") {
                  queryString = new URLSearchParams(window.location.search);
                  if (queryString.has("phone")) {
                    return queryString.get("phone");
                  } else {
                    return "";
                  }
                }
                return "";
              }}
              validateInput={validateInput}
            />
          </div>
        </div>

        <div
          sx={{
            marginTop: "25px",
            width: "100%",
          }}
        >
          <div
            sx={{
              width: "100%",
              marginTop: "15px",
            }}
          >
            <label
              htmlFor="address"
              sx={{
                width: "100%",
              }}
            >
              Address
            </label>
            <input
              ref={addressRef}
              required
              id="autocomplete"
              name="address"
              placeholder="Address"
              onChange={(e) => validateInput(e)}
              onBlur={(e) => validateInput(e)}
              onFocus={(e) => validateInput(e)}
              sx={{
                width: "100%",
                outline: "none",
                border: "solid 1px #ccc",
                borderRadius: "4px",
                fontSize: "16px",
                padding: "10px",
              }}
            ></input>
          </div>
          <div
            sx={{
              width: "100%",
              marginTop: "15px",
            }}
          >
            <label
              htmlFor="city"
              sx={{
                width: "100%",
              }}
            >
              City
            </label>
            <input
              type="text"
              required
              ref={cityRef}
              name="city"
              placeholder="City"
              onChange={(e) => validateInput(e)}
              onBlur={(e) => validateInput(e)}
              onFocus={(e) => validateInput(e)}
              sx={{
                width: "100%",
                outline: "none",
                border: "solid 1px #ccc",
                borderRadius: "4px",
                fontSize: "16px",
                padding: "10px",
              }}
            ></input>
          </div>
          <div
            sx={{
              width: "100%",
              marginTop: "15px",
            }}
          >
            <label
              htmlFor="state"
              sx={{
                width: "100%",
              }}
            >
              Province
            </label>
            <select
              name="state"
              required
              ref={stateRef}
              defaultValue=""
              onChange={(e) => validateInput(e)}
              onBlur={(e) => validateInput(e)}
              onFocus={(e) => validateInput(e)}
              sx={{
                width: "100%",
                backgroundColor: "#fff",
                borderRadius: "4px",
                fontSize: "16px",
                padding: "10px",
                border: "solid 1px #ccc",
              }}
            >
              <option value="" key={99} disabled>
                Select a province
              </option>
              {states.map((i: StateLookup) => {
                return (
                  <option value={i.abbr} key={i.abbr}>
                    {i.name}
                  </option>
                );
              })}
            </select>
          </div>
          <div
            sx={{
              width: "100%",
              marginTop: "15px",
            }}
          >
            <label
              htmlFor="zipCode"
              sx={{
                width: "100%",
              }}
            >
              Postal code
            </label>
            <input
              type="text"
              required
              ref={zipRef}
              name="zipCode"
              placeholder="Postal code"
              onChange={(e) => validateInput(e)}
              onBlur={(e) => validateInput(e)}
              onFocus={(e) => validateInput(e)}
              sx={{
                width: "100%",
                outline: "none",
                border: "solid 1px #ccc",
                borderRadius: "4px",
                fontSize: "16px",
                padding: "10px",
              }}
            ></input>
          </div>
        </div>

        <div
          sx={{
            marginTop: "25px",
            width: "100%",
          }}
        >
          <div
            sx={{
              marginTop: "25px",
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <input
              type="checkbox"
              required
              name="agreeterms"
              ref={communicationAgreeRef}
              onKeyUp={allFilled}
              onClick={allFilled}
              sx={{
                width: "20px",
                height: "20px",
                borderRadius: "3px",
                backgroundColor: "#fff",
                border: "solid 2px #ccc",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                backgroundSize: "75% 75%",
                flexShrink: 0,
                ":checked": {
                  borderColor: "brand.green",
                  backgroundImage: `url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3E%3Cpath fill='%2300502f' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26 2.974 7.25 8 2.193z'/%3E%3C/svg%3E")`,
                },
              }}
            ></input>
            <span sx={{ flexGrow: 1, paddingLeft: "20px" }}>
              By completing this online application, you are authorizing Cash
              Money to contact you by phone and email for a follow up. Online
              application only offered to residents of Alberta, British
              Columbia, Nova Scotia, Ontario and Saskatchewan.
              <br />
              <br />
              Please refer to our{" "}
              <a
                sx={{ variant: "a.default" }}
                href="/privacy-policy"
                title="Privacy Policy"
                target="_blank"
              >
                Privacy Policy
              </a>{" "}
              or{" "}
              <a
                sx={{ variant: "a.default" }}
                href="/contact-us/"
                title="Contact Us"
                target="_blank"
              >
                Contact Us
              </a>{" "}
              for more details
            </span>
          </div>
          <div
            sx={{
              marginTop: "25px",
              width: "100%",
              textAlign: "center",
            }}
          >
            <ReCAPTCHA
              size="compact"
              onChange={() => {
                setInputValid({ ...inputValid, recaptchaValid: true });
              }}
              onExpired={() => {
                setInputValid({ ...inputValid, recaptchaValid: false });
              }}
              sitekey={process.env.GATSBY_GOOGLE_RECAPTCHA_KEY ?? ""}
              sx={{ display: "inline-block" }}
            />
          </div>
          <input
            type="hidden"
            name="subID"
            value={shortAppDetails?.subID ?? ""}
          />
          <input
            type="hidden"
            name="RefererUrl"
            value={shortAppDetails?.refererUrl ?? ""}
          />
          <input
            type="hidden"
            name="source"
            value={shortAppDetails?.source ?? ""}
          />
          <input
            type="hidden"
            name="loanType"
            value={shortAppDetails?.loanType ?? ""}
          />
          <input
            type="hidden"
            name="leadType"
            value={shortAppDetails?.leadType ?? ""}
          />
          <input
            type="hidden"
            name="campaignID"
            value={shortAppDetails?.campaignID ?? ""}
          />
          <input
            type="hidden"
            name="StoreId"
            id="StoreId"
            value={props.storeId}
          />
          <input type="hidden" name="Status" value="Jared-Ignore" />
          <input
            type="hidden"
            name="ReportedAt"
            value={new Date().toLocaleString()}
          />
          <input type="hidden" name="ignoreCaptcha" value="False" />
          <input type="hidden" name="webVisitKey" />
        </div>

        <div
          sx={{
            marginTop: "25px",
            width: "100%",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {props.storeState == "British Columbia" ||
          props.storeState == "Ontario" ||
          props.storeState == "Nova Scotia" ? (
            <button
              sx={{
                my: "20px",
                mx: "auto",
                textAlign: "center",
                color: "#ED1B2E",
                textDecoration: "underline",
              }}
              onClick={() => props.setDisclaimerOpen(true)}
            >
              Back
            </button>
          ) : (
            ""
          )}
          <button
            ref={submitRef}
            onClick={(e) => processForm(e)}
            disabled={submitDisabled}
            sx={{
              variant: "buttons.primary",
              width: "100%",
            }}
          >
            {submitText}
          </button>
        </div>
      </form>
      <script
        src="https://www.google.com/recaptcha/api.js"
        defer
        asp-append-version="false"
      ></script>
    </div>
  );
};

export default ShortApp;
